import { UploadedFile } from "@cca/components/dist/types";
import { CourseStatus, UserCourseStatus } from "@core/enums";
import type { UserShortDto } from "@core/types";
import { bindAll } from "@core/utils";
import { type NavigateFunction } from "react-router-dom";
import { z } from "zod";

import AxiosService, { type TokenHandlers } from "./axiosService";

export const courseStatusSchema = z.nativeEnum(CourseStatus);
export const userCourseStatusSchema = z.nativeEnum(UserCourseStatus);

export type GetCoursesByStatusParams = {
  courseStatus: CourseStatus;
};

export type GetLearnerCoursesByStatusParams = {
  courseStatus: UserCourseStatus;
};

const unlimitFetchSize = -1;

export default class CourseUserService extends AxiosService {
  constructor(tokenHandlers: TokenHandlers, navigateTo: NavigateFunction) {
    super("courses", tokenHandlers, navigateTo);
    bindAll(this);
  }

  public getNotAssignedLearners(
    courseId: number,
    searchTerm: string,
    skip?: number,
    take = unlimitFetchSize,
  ) {
    return this.get<Array<UserShortDto>, unknown>(
      `${courseId}/not-assigned-learners`,
      {
        params: {
          searchTerm: searchTerm,
          skip: skip,
          take: take,
        },
      },
    );
  }

  public assign(
    courseId: number,
    learnerIds: number[],
    startDate: Date,
    endDate: Date,
    graderId?: number,
  ) {
    return this.post<boolean, unknown>(
      `${courseId}/assign-learners`,
      {
        graderId: graderId,
        learnerIds: learnerIds,
        startDate: startDate,
        endDate: endDate,
      },
      {},
    );
  }

  public enroll(courseId: number) {
    return this.post<boolean, unknown>(`${courseId}/enroll-user`, null);
  }

  public assignCsv(
    courseId: number,
    startDate: Date,
    endDate: Date,
    file: UploadedFile,
  ) {
    const formData = new FormData();
    formData.append("startDate", startDate.toISOString());
    formData.append("endDate", endDate.toISOString());
    formData.append("csvFile", file as Blob);
    return this.post<boolean, unknown>(
      `${courseId}/assign-learners-csv`,
      formData,
      { headers: { "Content-Type": "multipart/formData" } },
    );
  }

  public unassign(courseId: number, learnerIds: number[]) {
    return this.post<boolean, unknown>(
      `${courseId}/unassign-learners`,
      {
        learnerIds: learnerIds,
      },
      {},
    );
  }
}
